import Vue from 'vue';
import { Commit, Dispatch } from 'vuex';
import EventEntryModel, {
  create as createEntry,
  EmbeddableEntryData,
  EventEntryData
} from "../../models/EventEntryModel";
import EventModel, { create, EventData } from "../../models/EventModel";
import { create as createUploadedFile } from "../../models/UploadedFile";
import {create as createUser} from "../../models/UserModel";
import { ModelErrors } from "../../models/Model";
import divisions from "./divisions";
import files from "./files";
import cart from "./cart";
import waivers from "./waivers";
import ApiService from "../../lib/api";

interface EventsState {
  events: { [propName: number]: EventModel }

  eventIds: Array<number>;

  unsaved: Array<number>;

  entries: { [propName: string]: EventEntryModel};
  entrySKUs: { [sku: string]: string};
}

interface EventsActions {
  getters: EventsGetters,
  commit: Commit,
  dispatch: Dispatch
}

interface ApiModelErrors {
  reference: string;
  errors: { field: string, message: string }[]
}

interface EventsGetters {
  myEvents(limit: number): EventModel[];

  activeEvents(limit: number): EventModel[];

  byId(id: number): EventModel | null;

  getEntriesByEvent(id: number): EventEntryModel[];

  getEntryByRef(ref: string): EventEntryModel | null;
}

const state: EventsState = {
  events: {},
  eventIds: [],
  unsaved: [],
  entries: {},
  entrySKUs: {},
};

const mutations = {
  addEvent(store: EventsState, payload: EventModel): void {
    if (payload.data.id && store.eventIds.indexOf(payload.data.id) === -1) {
      Vue.set(store.events, payload.data.id, payload);
      store.eventIds.push(payload.data.id);
    }
  },
  replaceEventData(store: EventsState, payload: EventData): void {
    if (payload.id && store.events[payload.id]) {
      for (const i in payload) {
        Vue.set(store.events[payload.id].data, i, payload[i]);
      }
    }
  },
  replaceEntryData(store: EventsState, payload: EventEntryData): void {
    if (store.entries[payload.reference]) {
      for (const i in payload) {
        Vue.set(store.entries[payload.reference].data, i, payload[i]);
      }

      Vue.set(store.entrySKUs, payload.SKU, payload.reference);

      Vue.set(store.entries[payload.reference], 'errors', {});

      if (payload._embedded && payload._embedded.competitor) {
        Vue.set(store.entries[payload.reference], 'competitor', createUser(payload._embedded.competitor));
      }

      if (payload?._embedded?.attachments) {
        store.entries[payload.reference].updateAttachments(payload._embedded.attachments);
      }

      if (payload?._embedded?.videos) {
        store.entries[payload.reference].updateVideos(payload._embedded.videos);
      }

      if (payload?._embedded?.waiver) {
        store.entries[payload.reference].updateWaiver(payload._embedded.waiver);
      }

      if (payload?._embedded?.order_item) {
        store.entries[payload.reference].updateOrderItem(payload._embedded.order_item);
      }
    }
  },
  updateEntryStatus(store: EventsState, payload: {reference: string, approved?: string, paid?: boolean}): void {
    if (payload.reference && store.entries[payload.reference]) {
      const entry = store.entries[payload.reference];
      if (payload.approved !== undefined) {
        entry.data.approved = payload.approved;
      }

      if (payload.paid !== undefined) {
        entry.data.paid = payload.paid;
      }
    }
  },
  setEntryErrors(store: EventsState, payload: ApiModelErrors): void {
    if (payload.reference && store.entries[payload.reference]) {
      const entry = store.entries[payload.reference];
      Vue.set(entry, 'errors', {}); // Reset errors
      for (const i in payload.errors) {
        Vue.set(entry.errors, payload.errors[i].field, payload.errors[i].message);
      }
    }
  },
  removeEvent(store: EventsState, payload: number): void {
    if (store.events[payload]) {
      delete store.events[payload];
      store.eventIds.splice(store.eventIds.indexOf(payload), 1);
    }
  },
  addEntry(store: EventsState, payload: EventEntryModel): void {
    if (payload?.data?.reference) {
      Vue.set(store.entries, payload.data.reference, payload);

      if (payload?.data?.SKU) {
        Vue.set(store.entrySKUs, payload.data.SKU, payload.data.reference);
      }
    }
  },
  setEntryIdOnRef(store: EventsState, payload: EventEntryData): void {
    if (payload.reference && store.entries[payload.reference]) {
      store.entries[payload.reference].data.id = payload.id;

      if (payload?.SKU) {
        Vue.set(store.entrySKUs, payload.SKU, payload.reference);
      }
    }
  },
  removeEntry(store: EventsState, payload: EventEntryModel): void {
    if (store.entries[payload.data.reference]) {
      Vue.delete(store.entries, payload.data.reference)
    }
  },
  clearEntries(store: EventsState): void {
    Vue.set(store, 'entries', {});
    Vue.set(store, 'entrySKUs', {});
  }
};

const actions = {
  fetchMyOrganizerEvents({commit}: EventsActions, organizerId: number): void {
    ApiService.get('/events/organizers/' + organizerId + '/events').then((resp: ApiResponseArray<EventData>) => {
      for (const i in resp.data) {
        const event = create(resp.data[i]);
        commit('addEvent', event);
      }
    });
  },
  fetchActiveEvents({commit}: EventsActions): void {
    ApiService.get('/events').then((resp: ApiResponseArray<EventData>) => {
      for (const i in resp.data) {
        const event = create(resp.data[i]);
        commit('addEvent', event);
      }
    });
  },
  fetchEventById({commit}: EventsActions, eventID: number): Promise<void> {
    return new Promise((resolve, reject) => {
      ApiService.get(`/events/${eventID}`).then((resp: ApiResponseSingle<EventData>) => {
        if (resp.data) {
          const event = create(resp.data);
          commit('addEvent', event);
          resolve();
        }
      }).catch(() => {
        reject();
      });
    });
  },
  deleteEvent({commit}: EventsActions, payload: number): Promise<void> {
    return new Promise((resolve, reject) => {
      ApiService.delete('/events/' + payload).then((resp: ApiResponseSingle<EventData>) => {
        commit('removeEvent', payload);
        resolve();
      }).catch((resp: ApiResponseError) => {
        reject(resp.json.errors[0].message);
      });
    });
  },
  updateEvent({commit}: EventsActions, payload: EventModel): Promise<any> {
    return new Promise((resolve, reject) => {
      ApiService.patch('/events/' + payload.data.id, payload.data).then((resp: ApiResponseSingle<EventData>) => {
        commit('replaceEventData', resp.data);
        resolve(resp);
      }).catch((err: ApiResponseError) => {
        reject(err);
      });
    });
  },
  createEvent({commit}: EventsActions, payload: EventModel): Promise<any> {
    return new Promise((resolve, reject) => {
      ApiService.post('/events', payload.data).then((resp: ApiResponseSingle<EventData>) => {
        commit('addEvent', create(resp.data));
        resolve(resp);
      }).catch((err: ApiResponseError) => {
        reject(err);
      });
    });
  },
  addEntry({commit}: EventsActions, payload: EventEntryModel): void {
    commit('addEntry', payload);
  },
  copyEntry({commit, getters}: EventsActions, payload: { fromRef: string, toRef: string }): void {
    const from = getters.getEntryByRef(payload.fromRef) as EventEntryModel;
    const to = getters.getEntryByRef(payload.toRef) as EventEntryModel;

    const data: {[propName: string]: unknown} = {
      reference: to.data.reference
    };
    for (const i in from.data) {
      if (['attachment_references', 'id', 'division_id', 'approved', 'reference', 'backup_video_url', 'paid'].indexOf(i) > -1) {
        continue;
      }

      data[i] = from.data[i];
    }

    if (from.competitor) {
      data._embedded = {competitor: from.competitor.data};
    }
    commit('replaceEntryData', data);
  },
  saveEntry({commit, getters}: EventsActions, payload: EventEntryModel): Promise<void> {
    return new Promise((resolve, reject) => {
        const action = payload.data.id ? 'patch' : 'put';
        ApiService[action]('/events/' + payload.data.event_id + '/entries/' + payload.data.reference, payload.data).then((resp: ApiResponseSingle<EventEntryData>) => {
          commit('replaceEntryData', resp.data);
          commit('cart/removeItem', resp.data.SKU);
          resolve();
        }).catch((err: JQueryXHR) => {
          if (err.responseJSON.data) {
            const data = err.responseJSON.data as EventEntryData;
            commit('updateEntryStatus', {reference: data.reference, approved: data.approved, paid: data.paid});
          }
          commit('setEntryErrors', {reference: payload.data.reference, errors: err.responseJSON.errors});
          reject();
        });
    });
  },
  deleteEntry({commit, dispatch}: EventsActions, payload: EventEntryModel): Promise<void> {
    return new Promise((resolve, reject) => {
      if (!payload.data.id) {
        commit('removeEntry', payload);
        resolve();
      }
      ApiService.delete('/events/' + payload.data.event_id + '/entries/' + payload.data.reference).then((resp: ApiResponseSingle<EventEntryData>) => {
        dispatch('cart/removeItem', payload.data.SKU);

        if (resp?.data && resp.data.approved === "withdrawn") {
          commit('replaceEntryData', resp.data);
        } else {
          commit('removeEntry', payload);
        }
        resolve();
      }).catch((jXHR: JQueryXHR) => {
        reject(jXHR.responseJSON.errors[0].message);
      });
    });
  },
  refundEntry({commit, dispatch}: EventsActions, payload: EventEntryModel): Promise<void> {
    return new Promise((resolve, reject) => {
      ApiService.post('/events/orders/refund-item/' + payload.data.SKU, {}).then((resp: ApiResponseSingle<EventEntryData>) => {
        commit('replaceEntryData', resp.data);
        resolve();
      }).catch((jXHR: JQueryXHR) => {
        reject(jXHR.responseJSON.errors[0].message);
      });
    });
  },
  fetchMyEntries({getters, commit, dispatch}: EventsActions, eventId: number): Promise<any> {
    return new Promise((resolve, reject) => {
      ApiService.get('/events/' + eventId + '/entries?filter=mine&validate=1').then((resp: ApiResponseArray<EventEntryData>) => {
        for (const entry of resp.data) {
          const errors: ModelErrors = {};
          if (entry?._embedded?.errors) {
            for (const e of entry._embedded.errors) {
              errors[e.field] = e.message;
            }
          }
          commit('addEntry', createEntry(entry, errors));

          if (entry?._embedded?.attachments) {
            for (const a of entry._embedded.attachments) {
              dispatch('files/addExisting', createUploadedFile(a), { root: true });
            }
          }
        }
        resolve(resp.data);
      }).catch(() => {

      });
    });
  },
  clearEntries({commit}: EventsActions) : void {
    commit('clearEntries');
  },
  syncEntryFromServer({commit}: EventsActions, ref: string): Promise<void> {
    return new Promise((resolve, reject) => {
      ApiService.get('/events/entries/' + ref).then((resp: ApiResponseSingle<EventEntryData>) => {
        commit('replaceEntryData', resp.data);
        commit('cart/removeItem', resp.data.SKU);
        resolve();
      }).catch((err: any) => {
        reject();
      });
    })
  }
};

const getters = {
  myEvents: (state: EventsState) => (limit: number) => {
    return state.eventIds.map(id => state.events[id]);
  },
  activeEvents: (state: EventsState) => (limit = -1) => {
    const active = [];
    for (const i in state.events) {
      if (state.events[i].data.status === 'active') {
        active.push(state.events[i]);
      }
    }
    return active;
  },
  byId: (state: EventsState) => (id: number) => {
    return (state.events[id]) ? state.events[id] : null;
  },
  getEntriesByEvent: (state: EventsState) => (id: number): EventEntryModel[] => {
    const entries = [];
    for (const i in state.entries) {
      if (state.entries[i].data.event_id === id) {
        entries.push(state.entries[i]);
      }
    }
    return entries;
  },
  getMyEntriesByEvent: (state: EventsState) => (id: number, userId: number): EventEntryModel[] => {
    const entries = [];
    for (const i in state.entries) {
      if (state.entries[i].data.event_id === id && state.entries[i].data.user_id === userId) {
        entries.push(state.entries[i]);
      }
    }
    return entries;
  },
  getEntryById: (state: EventsState) => (id: number): EventEntryModel | null => {
    for (const i in state.entries) {
      if (state.entries[i].data.id === id) {
        return state.entries[i];
      }
    }

    return null;
  },
  getEntryBySKU: (state: EventsState, getters: any) => (sku: string): EventEntryModel | null => {
    if (state.entrySKUs[sku]) {
      return getters.getEntryByRef(state.entrySKUs[sku]);
    }

    return null;
  },
  getEntryByRef: (state: EventsState) => (ref: string): EventEntryModel | null => {
    if (state.entries[ref]) {
      return state.entries[ref];
    }

    return null;
  },
};

export default {
  namespaced: true,
  state,
  mutations,
  actions,
  getters,
  modules: {
    divisions,
    files,
    cart,
    waivers,
  }
};
